/**
 * ProtectedRoute / PublicRoute / PostLoginLanding
 *
 * RC 1.39.B-hotfix (2026-04-26) — ALL splash screens removed from
 * the system per explicit product directive. No `<LoadingScreen/>`,
 * no spinners, no minimum-display holds, no `splashHoldExpired`
 * gate, no full-screen loading state. The route guards now render
 * `null` (an invisible empty fragment) during the brief bootstrap
 * window where neither a redirect nor the children can be shown
 * yet. With RC 1.38.D2's de-recursed bootstrap and RC 1.39.A's
 * 600 ms target, that window is essentially imperceptible.
 *
 * Authoritative product rule: splash MUST NOT appear during
 * onboarding, CV upload, preferences, form saves, internal wizard
 * navigation, document upload, or dashboard interactions. The
 * easiest way to honour that is to not have a splash at all.
 */

import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace, landingPathFor } from '@/contexts/WorkspaceContext';
import { hasEmployerInviteBypassFor } from '@/lib/employerInviteBypass';

/* Safety: only honour internal same-origin returnTo paths to
 * prevent open-redirect via crafted ?returnTo=https://evil. */
function safeReturnTo(raw) {
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith('/') && !decoded.startsWith('//')) return decoded;
  } catch { /* fall-through */ }
  return null;
}

export function ProtectedRoute({ children, requireProfile = true, redirectIfProfile = false }) {
  const { user, hasProfile, profileChecked, loading } = useAuth();
  const location = useLocation();

  // Bootstrap window — render nothing rather than make a wrong
  // redirect decision. Lasts a frame or two; user never sees it.
  if (loading) return null;
  if (user && !profileChecked) return null;

  if (!user) return <Navigate to="/login" replace />;

  if (requireProfile && !hasProfile) {
    // Phase 0.7.2 — employer-invitee onboarding bypass.
    // A fresh invitee has an `emp_account_members` row but no
    // `profiles` row. Forcing them through candidate onboarding
    // (which is what `/onboarding` is) is a UX dead-end. We honour
    // the session-scoped, user-tied bypass marker set by
    // `InvitationAcceptPage` ONLY for `/employer/*` destinations.
    // Any other destination still falls through to /onboarding.
    const isEmployerPath = (
      location.pathname === '/employer'
      || location.pathname.startsWith('/employer/')
    );
    const bypassActive = hasEmployerInviteBypassFor(user.id);
    if (!(isEmployerPath && bypassActive)) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  if (redirectIfProfile && hasProfile) return <Navigate to="/post-login" replace />;

  return children;
}

export function PublicRoute({ children }) {
  const { user, hasProfile, profileChecked, loading } = useAuth();
  const [params] = useSearchParams();

  if (loading) return null;
  if (user && !profileChecked) return null;

  if (user) {
    // Phase 0.7.1 — honour ?returnTo for safe internal paths
    // (e.g. /invitations/accept?token=...). Falls back to the
    // standard post-login / onboarding routing when absent.
    const ret = safeReturnTo(params.get('returnTo'));
    if (ret) return <Navigate to={ret} replace />;
    return <Navigate to={hasProfile ? '/post-login' : '/onboarding'} replace />;
  }

  return children;
}

/**
 * PostLoginLanding — workspace-aware landing redirect.
 *
 * Renders nothing during the brief access-resolution window.
 * Resolves to a Navigate as soon as the workspace context payload
 * arrives — the user never visually parks on this screen.
 */
export function PostLoginLanding() {
  const { access, loading, allowedWorkspaces, effectiveDefault } = useWorkspace();

  if (!access && loading) return null;
  if (!access)            return null;

  if (!allowedWorkspaces.length) {
    return <Navigate to="/no-access" replace />;
  }
  if (allowedWorkspaces.length === 1) {
    return <Navigate to={landingPathFor(allowedWorkspaces[0])} replace />;
  }
  if (effectiveDefault) {
    return <Navigate to={landingPathFor(effectiveDefault)} replace />;
  }
  return <Navigate to="/choose-workspace" replace />;
}
